import React, { useEffect, useRef, useState } from 'react';
import styles from './PlaceOrder.module.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { setMinutes, setHours } from 'date-fns';

import { DateTimeField } from './DateTimeField';
import { TealSwitch } from './common';

import d2dIcon from "../../images/place_order/d2d-icon.png";
import codIcon from "../../images/place_order/cod-icon.png";

const isSameDay = (a, b) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

export const StopDetailsPopover = (props) => {
  const { type, stopKey, ordinal, form, onCancel, onSave } = props;
  const isPickUp = type === "PICK_UP";

  const [unitNumber, setUnitNumber] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [personName, setPersonName] = useState("");
  const [deliverDateTime, setDeliverDateTime] = useState("");
  const [doorToDoor, setDoorToDoor] = useState(false);
  const [isCodEnabled, setIsCodEnabled] = useState(false);
  const [codAmount, setCodAmount] = useState(0);
  const [fieldError, setFieldError] = useState(null);

  const bodyRef = useRef(null);

  useEffect(() => {
    setFieldError(null);
    if (isPickUp) {
      setUnitNumber(form.pickUpLocation.unitNumber || "");
      setContactNumber((form.pickUpContactNumber || "").replace("+", ""));
      setPersonName(form.pickUpPersonName || "");
    } else {
      setUnitNumber(form.dropOffLocation.unitNumber || "");
      setContactNumber((form.recipientPhone || "").replace("+", ""));
      setPersonName(form.recipientName || "");
      setDeliverDateTime(form.deliverDateTime || "");
      setDoorToDoor(form.doorToDoor || false);
      setIsCodEnabled(form.isCodEnabled || false);
      setCodAmount(form.codAmount || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopKey]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const clearError = (field) => {
    if (fieldError && fieldError.field === field) setFieldError(null);
  };

  const submit = async () => {
    setFieldError(null);
    const result = isPickUp
      ? await onSave(unitNumber, contactNumber, personName)
      : await onSave(deliverDateTime, unitNumber, contactNumber, personName, doorToDoor, isCodEnabled, codAmount);

    if (result) {
      setFieldError(result);
      const node = bodyRef.current && bodyRef.current.querySelector(`[data-field="${result.field}"]`);
      if (node) {
        node.scrollIntoView({ block: "nearest" });
        const input = node.querySelector("input");
        if (input) input.focus();
      }
    }
  };

  const errorFor = (field) => (fieldError && fieldError.field === field ? fieldError.message : null);

  const fieldClass = (field) => `${styles.poFld} ${errorFor(field) ? styles.poFldErr : ""}`;

  const errorNote = (field) => {
    const message = errorFor(field);
    return message ? <p className={styles.poFldMsg}>{message}</p> : null;
  };

  const minDate = !isPickUp && form.pickUpDateTime && (new Date()).getTime() <= (new Date(form.pickUpDateTime)).getTime()
    ? new Date(form.pickUpDateTime)
    : new Date();

  const minTime = !isPickUp && (
    (form.pickUpDateTime && deliverDateTime === "") ||
    (form.pickUpDateTime && deliverDateTime && isSameDay(new Date(form.pickUpDateTime), new Date(deliverDateTime)))
  )
    ? setHours(setMinutes(new Date(), new Date(form.pickUpDateTime).getMinutes()), new Date(form.pickUpDateTime).getHours())
    : setHours(setMinutes(new Date(), 0), 6);

  return (
    <>
      <div className={styles.poPopBackdrop} onClick={onCancel} />

      <div className={styles.poPop} role="dialog" aria-modal="true">
        <div className={styles.poPopHead}>
          <div className={`${styles.poPin} ${isPickUp ? styles.poPinPickUp : styles.poPinDropOff}`}>{ordinal}</div>
          <h4>{isPickUp ? "Pick-up details" : "Drop-off details"}</h4>
        </div>

        <div className={styles.poPopBody} ref={bodyRef}>
          <div>
            <div className={fieldClass("personName")} data-field="personName">
              <label htmlFor="poPersonName">{isPickUp ? "Sender's name" : "Recipient's name"}</label>
              <input
                id="poPersonName"
                value={personName}
                onChange={(e) => { setPersonName(e.target.value); clearError("personName"); }}
                placeholder={isPickUp ? "Who is handing over the parcel?" : "Who is receiving it?"}
              />
            </div>
            {errorNote("personName")}
          </div>

          <div data-field="contactNumber">
            <label className={styles.poPhoneLbl}>Contact number</label>
            <div className={errorFor("contactNumber") ? styles.poPhoneErr : undefined}>
              <PhoneInput
                country={'my'}
                countryCodeEditable={false}
                preferredCountries={['my', 'sg', 'au', 'us']}
                autoFormat={false}
                value={contactNumber}
                onChange={(v) => { setContactNumber(v); clearError("contactNumber"); }}
                inputProps={{ required: true }}
                inputStyle={{ height: 42, width: "100%", fontSize: 13, fontWeight: 500 }}
              />
            </div>
            {errorNote("contactNumber")}
          </div>

          <div className={styles.poFld}>
            <label htmlFor="poUnitNumber">Unit / floor — optional</label>
            <input
              id="poUnitNumber"
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
              placeholder="e.g. Level 3, Lot 3.12"
            />
          </div>

          {
            !isPickUp &&
            <>
              <div>
                <div className={fieldClass("deliverDateTime")} data-field="deliverDateTime">
                  <label htmlFor="deliverDateTime">Deliver by</label>
                  <DateTimeField
                    inputId="deliverDateTime"
                    className={styles.poAddrInput}
                    value={deliverDateTime}
                    onChange={(date) => { setDeliverDateTime(date); clearError("deliverDateTime"); }}
                    minDate={minDate}
                    minTime={minTime}
                    maxTime={setHours(setMinutes(new Date(), 0), 23)}
                    placeholder="Select a delivery time"
                  />
                </div>
                {errorNote("deliverDateTime")}
              </div>

              <div>
                <div className={styles.poPopOpt}>
                  <img src={d2dIcon} alt="door-to-door" />
                  <span>Door-to-door</span>
                  <div style={{ marginLeft: "auto" }}>
                    <TealSwitch checked={doorToDoor} onChange={() => setDoorToDoor(!doorToDoor)} size="small" />
                  </div>
                </div>
                <div className={styles.poPopOpt}>
                  <img src={codIcon} alt="cash on delivery" />
                  <span>Cash on delivery</span>
                  <div style={{ marginLeft: "auto" }}>
                    <TealSwitch checked={isCodEnabled} onChange={() => setIsCodEnabled(!isCodEnabled)} size="small" />
                  </div>
                </div>
              </div>

              {
                isCodEnabled &&
                <div>
                  <div className={fieldClass("codAmount")} data-field="codAmount">
                    <label htmlFor="poCodAmount">COD amount (RM)</label>
                    <input
                      id="poCodAmount"
                      type="number"
                      min={0}
                      value={codAmount}
                      onChange={(e) => { setCodAmount(e.target.value); clearError("codAmount"); }}
                      placeholder="0.00"
                    />
                  </div>
                  {errorNote("codAmount")}
                </div>
              }
            </>
          }
        </div>

        <div className={styles.poPopFoot}>
          <button className={styles.poBtnGhost} onClick={onCancel}>Cancel</button>
          <button className={styles.poBtnSolid} onClick={submit}>Save</button>
        </div>
      </div>
    </>
  );
};
